import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAthlete } from '../services/api';
import { getTechniqueById } from '../utils/atcTechniques';
import Flag from './ui/Flag';
import PageLayout from './ui/PageLayout';

function techName(id) {
  return getTechniqueById(id)?.name || id;
}

function AthleteProfilePage() {
  const navigate = useNavigate();
  const { name } = useParams();
  const decodedName = decodeURIComponent(name);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getAthlete(decodedName)
      .then(d => setData(d))
      .catch(() => setError('Impossibile caricare il profilo.'))
      .finally(() => setLoading(false));
  }, [decodedName]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary)', color: 'var(--color-text-dim)', fontSize: 14 }}>
        Caricamento profilo…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary)', color: 'var(--color-text)', gap: 16 }}>
        <div style={{ color: 'var(--color-red)' }}>{error || 'Atleta non trovato'}</div>
        <button style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', cursor: 'pointer' }} onClick={() => navigate('/atleti')}>
          ← Database Atleti
        </button>
      </div>
    );
  }

  const winRate     = data.matches > 0 ? Math.round((data.wins / data.matches) * 100) : 0;
  const successRate = data.totalActions > 0 ? Math.round((data.totalScoringActions / data.totalActions) * 100) : 0;
  const ptPerMatch  = data.matches > 0 ? (data.ptScored / data.matches).toFixed(1) : '—';

  const techEntries = Object.entries(data.techniqueCount || {})
    .map(([tech, count]) => ({
      tech,
      label: techName(tech),
      count,
      points: (data.techniquePoints || {})[tech] || 0,
    }))
    .sort((a, b) => b.count - a.count);

  const maxTechCount = techEntries.length > 0 ? techEntries[0].count : 1;

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/atleti')}>
        ← Atleti
      </button>
      <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.3 }}>Profilo Atleta</span>
    </div>
  );

  return (
    <PageLayout header={header}>

        {/* Profile header */}
        <div className="ds-card" style={{ padding: '24px 28px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <Flag nat={data.nationality} size={32} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>{data.name}</div>
              {data.nationality && <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 2 }}>{data.nationality}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {(data.weightCategories || []).map(w => (
              <span key={w} style={{ fontSize: 10, background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '2px 8px', color: 'var(--color-text-dim)' }}>{w}</span>
            ))}
            {(data.events || []).map(e => (
              <span key={e} style={{ fontSize: 10, background: 'var(--accent-bg)', borderRadius: 4, padding: '2px 8px', color: 'var(--accent)' }}>{e}</span>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Incontri', value: data.matches },
            { label: 'Vittorie', value: data.wins, color: 'var(--color-green)' },
            { label: 'Sconfitte', value: data.losses, color: 'var(--color-red)' },
            { label: '% Vittoria', value: `${winRate}%`, color: winRate >= 60 ? 'var(--color-green)' : winRate >= 40 ? 'var(--color-orange)' : 'var(--color-red)' },
            { label: 'Pt/Incontro', value: ptPerMatch, color: 'var(--color-blue)' },
            { label: 'Success Rate', value: `${successRate}%`, color: 'var(--accent)' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)', padding: '16px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: color || 'var(--color-text)', letterSpacing: -1 }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 3 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Two columns: Technique breakdown + Action type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

          {/* Technique breakdown */}
          <div className="ds-card" style={{ padding: 20 }}>
            <div className="ds-section-label" style={{ marginBottom: 14 }}>TECNICHE UTILIZZATE</div>
            {techEntries.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--color-text-dim)', textAlign: 'center', padding: 16 }}>Nessun dato</div>
            ) : techEntries.map(({ tech, label, count, points }) => {
              const pct = Math.round((count / maxTechCount) * 100);
              return (
                <div key={tech} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-text)' }}>{label}</span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
                      {count}x
                      {points > 0 && <span style={{ color: 'var(--color-green)', marginLeft: 6 }}>+{points}pt</span>}
                    </span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2 }}>
                    <div style={{ height: 4, width: `${pct}%`, background: 'var(--accent)', borderRadius: 2, opacity: points > 0 ? 1 : 0.4 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action type + points */}
          <div className="ds-card" style={{ padding: 20 }}>
            <div className="ds-section-label" style={{ marginBottom: 14 }}>DISTRIBUZIONE AZIONI</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Attacchi', value: data.totalAttacks, color: 'var(--color-blue)' },
                { label: 'Difese', value: data.totalDefenses, color: 'var(--color-green)' },
                { label: 'Contrattacchi', value: data.totalCounters, color: 'var(--color-orange)' },
              ].map(({ label, value, color }) => {
                const pct = data.totalActions > 0 ? Math.round((value / data.totalActions) * 100) : 0;
                return (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: 'var(--color-text)' }}>{label}</span>
                      <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{value} ({pct}%)</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2 }}>
                      <div style={{ height: 4, width: `${pct}%`, background: color, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 24, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
              <div className="ds-section-label" style={{ marginBottom: 10 }}>PUNTI AGGREGATI</div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-blue)' }}>{data.ptScored}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>Segnati</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-red)' }}>{data.ptConceded}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>Subiti</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: (data.ptScored - data.ptConceded) >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                    {(data.ptScored - data.ptConceded) >= 0 ? '+' : ''}{data.ptScored - data.ptConceded}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>Differenza</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Match history */}
        <div className="ds-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)' }}>
            <span className="ds-section-label">
              STORICO INCONTRI ({(data.sessions || []).length})
            </span>
          </div>
          {(data.sessions || []).length === 0 ? (
            <div className="ds-empty-state">Nessun incontro trovato.</div>
          ) : (data.sessions || []).map((s, i) => {
            const side     = s.athlete_blue_name === decodedName ? 'blue' : 'red';
            const opp      = side === 'blue' ? 'red' : 'blue';
            const oppName  = s[`athlete_${opp}_name`];
            const oppNat   = s[`athlete_${opp}_nationality`];
            const myScore  = Number(s[`score_${side}`]) || 0;
            const oppScore = Number(s[`score_${opp}`])  || 0;
            const won      = myScore > oppScore;
            const lost     = oppScore > myScore;
            const resultLabel = won ? 'V' : lost ? 'S' : 'P';
            const resultColor = won ? 'var(--color-green)' : lost ? 'var(--color-red)' : 'var(--color-text-dim)';

            return (
              <div
                key={s.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 20px',
                  borderBottom: i < data.sessions.length - 1 ? '1px solid var(--color-border)' : 'none',
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onClick={() => navigate(`/report/${s.id}`)}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: won ? 'rgba(52,211,153,0.15)' : lost ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)',
                }}>
                  <span style={{ color: resultColor, fontSize: 12, fontWeight: 800 }}>{resultLabel}</span>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--color-text-dim)', fontSize: 11 }}>vs</span>
                    <Flag nat={oppNat} />
                    <span>{oppName}</span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 2, display: 'flex', gap: 8 }}>
                    {s.event_name && <span>{s.event_name}</span>}
                    {s.weight_category && <span>{s.weight_category}</span>}
                    {s.event_date && <span>{s.event_date}</span>}
                  </div>
                </div>

                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>
                  <span style={{ color: won ? 'var(--color-green)' : 'var(--color-text)' }}>{myScore}</span>
                  <span style={{ color: 'var(--color-text-dim)', margin: '0 4px', fontSize: 12 }}>:</span>
                  <span style={{ color: lost ? 'var(--color-red)' : 'var(--color-text)' }}>{oppScore}</span>
                </div>

                <span style={{ color: 'var(--color-text-dim)', fontSize: 12 }}>→</span>
              </div>
            );
          })}
        </div>
    </PageLayout>
  );
}

export default AthleteProfilePage;
